/**
 * Admin V3 — Financial / Withdrawals — 4-card summary strip
 *
 * Source: GET /admin/economy/withdrawals/summary (admin_economy.py:213-245)
 *
 * Backend returns 4 status buckets only: pending, processing, completed, failed.
 * The `reversed` and `cancelled` status totals are not in this summary by design.
 *
 * Failed card adopts alert tone (rose) when count > 0.
 */

import React from 'react';
import { Clock, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { cn, currency } from '../../shared/visualTokens';
import { t } from '../../i18n';

const SummaryCard = ({ icon: Icon, label, count, amount, alertTone = false, loading, testId }) => (
  <div
    className={cn(
      'rounded-2xl border bg-white p-4 shadow-[0_4px_18px_rgba(15,23,42,0.04)]',
      alertTone ? 'border-rose-200' : 'border-slate-200'
    )}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-2">
      <div className={cn(
        'flex h-7 w-7 items-center justify-center rounded-lg',
        alertTone ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-500'
      )}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </span>
    </div>
    {loading ? (
      <>
        <div className="h-7 w-12 rounded bg-slate-100 animate-pulse mb-1" />
        <div className="h-3 w-20 rounded bg-slate-100 animate-pulse" />
      </>
    ) : (
      <>
        <div className={cn('text-2xl font-bold tabular-nums', alertTone ? 'text-rose-700' : 'text-slate-900')}>
          {count}
        </div>
        <div className="text-[11px] text-slate-500 tabular-nums">
          {currency(amount)}
        </div>
      </>
    )}
  </div>
);

const FinancialWithdrawalsSummary = ({ data, loading, locale = 'fr' }) => {
  const payload = data && typeof data === 'object' ? data : {};
  const pending    = payload.pending    || { count: 0, amount: 0 };
  const processing = payload.processing || { count: 0, amount: 0 };
  const completed  = payload.completed  || { count: 0, amount: 0 };
  const failed     = payload.failed     || { count: 0, amount: 0 };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="adminv3-financial-withdrawals-summary">
      <SummaryCard
        icon={Clock}
        label={t('financial.withdrawals.summary.pending', locale)}
        count={pending.count ?? 0}
        amount={pending.amount ?? 0}
        loading={loading}
        testId="adminv3-financial-withdrawals-summary-pending"
      />
      <SummaryCard
        icon={Loader2}
        label={t('financial.withdrawals.summary.processing', locale)}
        count={processing.count ?? 0}
        amount={processing.amount ?? 0}
        loading={loading}
        testId="adminv3-financial-withdrawals-summary-processing"
      />
      <SummaryCard
        icon={CheckCircle2}
        label={t('financial.withdrawals.summary.completed', locale)}
        count={completed.count ?? 0}
        amount={completed.amount ?? 0}
        loading={loading}
        testId="adminv3-financial-withdrawals-summary-completed"
      />
      <SummaryCard
        icon={XCircle}
        label={t('financial.withdrawals.summary.failed', locale)}
        count={failed.count ?? 0}
        amount={failed.amount ?? 0}
        alertTone={(failed.count ?? 0) > 0}
        loading={loading}
        testId="adminv3-financial-withdrawals-summary-failed"
      />
    </div>
  );
};

export default FinancialWithdrawalsSummary;
